/**
 * Map a backend order status string to the SPA `OrderStatusDisplay`.
 *
 * Extracted as a pure module (GEN-2998, D1 fix) so it can be unit-tested
 * without importing the ~2,700-LOC `<OrderInProgress>` page and its heavy
 * dependency graph (e.g. `pdfjs-dist`). Re-exported from `OrderInProgress`
 * for backwards compatibility.
 *
 * This mapper feeds the comparator edit gate (`canEditComparatorParents`).
 * The honesty-critical rule: missing/unknown backend statuses are treated as
 * non-editable (`CANCELLED`) — safe-by-default so a genuinely unknown or
 * failed-to-fetch status never shows edit affordances the server would reject
 * with HTTP 409. `DRAFT` is pre-accession and therefore editable
 * (`ORDER_PLACED`); `CANCELLED` is terminal/non-editable.
 */

import { OrderStatusDisplay } from '@/types/NavigationTypes'
import { OrderStatus } from '@/utils/DatabaseOperations'

export const mapOrderStatusToDisplay = (backendStatus?: string): OrderStatusDisplay => {
  if (!backendStatus) return OrderStatusDisplay.CANCELLED

  switch (backendStatus) {
    case OrderStatus.DRAFT:
      return OrderStatusDisplay.ORDER_PLACED
    case OrderStatus.PENDING:
      return OrderStatusDisplay.ORDER_PLACED
    case OrderStatus.SUBMITTED:
      return OrderStatusDisplay.ORDER_SUBMITTED
    case OrderStatus.ACCESSIONED:
      return OrderStatusDisplay.SAMPLE_RECEIVED
    case OrderStatus.COMPLETE:
      return OrderStatusDisplay.RESULT_READY
    case OrderStatus.REANALYSIS:
      // A reanalysis order is a completed order with an in-flight reanalysis;
      // surface it like a completed order (results remain viewable) rather than
      // falling through to the CANCELLED default. Reanalysis-specific UI is
      // driven by the ?reanalysis=true action param, not the persisted status.
      return OrderStatusDisplay.RESULT_READY
    case OrderStatus.CANCELLED:
      return OrderStatusDisplay.CANCELLED
    default:
      return OrderStatusDisplay.CANCELLED
  }
}
